import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useLayoutEffect, useState } from 'react'
import NotFoundResult from '@/app/common/components/NotFoundResult'
import {
  EItemCategorySearchIcon,
  SearchPageItemCategoryFragmentFragment,
  SearchPageProductFragmentFragment,
} from '@/types/gql/graphql'
import { cn } from '@/utils/cn'
import Car from './assets/car.svg'
import Pawn from './assets/pawn.svg'
import Purchase from './assets/purchase.svg'

type SearchAutocompleteProps = {
  isFetching: boolean
  searchTerm: string
  products: SearchPageProductFragmentFragment[]
  categories: SearchPageItemCategoryFragmentFragment[]
}

export function SearchResults({
  isFetching,
  searchTerm,
  products,
  categories,
}: SearchAutocompleteProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const [selectedItemIndex, setSelectedItemIndex] = useState<
    number | undefined
  >()

  useLayoutEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const totalItems = products.length + categories.length
      switch (event.key) {
        case 'ArrowDown':
          setSelectedItemIndex((current) => {
            if (current === undefined || current === totalItems - 1) {
              return 0
            } else {
              return current + 1
            }
          })
          break

        case 'ArrowUp':
          setSelectedItemIndex((current) => {
            if (current === undefined || current === 0) {
              return totalItems - 1
            } else {
              return current - 1
            }
          })
          break
        case 'Enter':
          if (!selectedItemIndex) return
          if (products.length > 0 && selectedItemIndex < products.length) {
            router.push(`/products/${products[selectedItemIndex].slug}`)
          } else {
            router.push(
              categories[selectedItemIndex - products.length].fullSlug,
            )
          }
          break
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [products, categories, router, selectedItemIndex])

  const splitAroundMatchesWithSearchTerm = useCallback(
    (title: string) => {
      // escape all incoming regexes (https://github.com/sindresorhus/escape-string-regexp)
      const escapedInput = searchTerm
        .trim()
        .replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')
        .replace(/-/g, '\\x2d')
        // match searched words individually as groups, instead of the whole search term
        .replace(/\s+/g, '|')
      const highlightMatchesRegex = new RegExp(`(${escapedInput})`, 'gi')
      return title.split(highlightMatchesRegex).map((part) => ({
        part,
        isMatch: highlightMatchesRegex.test(part),
      }))
    },
    [searchTerm],
  )

  if (!searchTerm) return null

  if (isFetching && products.length === 0 && categories.length === 0) {
    return null
  }

  if (products.length === 0 && categories.length === 0) {
    return <NotFoundResult searchedItem={searchTerm} />
  }

  return (
    <div className="flex flex-col gap-4">
      {products.length > 0 && (
        <div>
          <p className="text-xs text-[#6C6D6F] font-semibold uppercase px-4 mb-2">
            {t('common:top_results')}
          </p>
          <ul className="divide-y divide-gray-200">
            {products.map((item, index) => (
              <li
                key={item.slug}
                className={cn(
                  'hover:bg-gray-100',
                  index === selectedItemIndex && 'bg-gray-100',
                )}
              >
                <Link
                  className="block px-4 py-2"
                  href={`/products/${item.slug}`}
                  onClick={() => {
                    setSelectedItemIndex(index)
                  }}
                >
                  {splitAroundMatchesWithSearchTerm(item.name).map(
                    ({ part, isMatch }, i) => (
                      <span className={cn(isMatch && 'font-semibold')} key={i}>
                        {part}
                      </span>
                    ),
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {categories.length > 0 && (
        <div>
          <p className="text-xs text-[#6C6D6F] font-semibold uppercase px-4 mb-2">
            {t('common:other_results')}
          </p>
          <ul className="divide-y divide-gray-200">
            {categories.map((item, index) => {
              const adjustedIndex = index + products.length

              return (
                <li
                  key={item.fullSlug}
                  className={cn(
                    'hover:bg-gray-100',
                    adjustedIndex === selectedItemIndex && 'bg-gray-100',
                  )}
                >
                  <Link
                    className="flex items-center gap-2 px-4 py-2"
                    href={item.fullSlug}
                    onClick={() => {
                      setSelectedItemIndex(adjustedIndex)
                    }}
                  >
                    {item.icon && <Icon icon={item.icon} />}
                    <span className="text-sm font-semibold text-black">
                      {item.text.text}
                    </span>
                    {item.matchedKeyword && (
                      <span className="text-xs text-[#6C6D6F]">
                        - {item.matchedKeyword}
                      </span>
                    )}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function Icon({ icon }: { icon: EItemCategorySearchIcon }) {
  const Comp = resolveIcon(icon)
  if (!Comp) return null
  return <div className="size-9 flex items-center justify-center">{Comp}</div>
}

function resolveIcon(icon: EItemCategorySearchIcon) {
  switch (icon) {
    case EItemCategorySearchIcon.Pawn:
      return <Pawn />
    case EItemCategorySearchIcon.Purchase:
      return <Purchase />
    case EItemCategorySearchIcon.Car:
      return <Car />
    default:
      return null
  }
}
